import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';
import { getConditionalObject } from 'utils/data';

import DateFieldCDS from '../../form/DateFieldCDS';
import { ArrowCheckIcon, ErrorFilledIcon } from '../../icons';
import FilterPlaceholderCDS from '../FilterPlaceholderCDS';
import { TableTestIds } from '../Table.testIds';

import DecoratorOption, { isDecoratorOption } from './components/DecoratorOption';
import { DateTimezoneLabels, DEFAULT_SELECT_OPTIONS, FilterTypes } from './FilterBaseCDS.constants';
import {
  anchorOrigin,
  dateRangeContentStyles,
  dateRangeFieldStyles,
  dateRangeMenuControlStyles,
  dateRangeSeparatorStyles,
  dateTimezoneSwitchStyles,
  filterMenuStyles,
  getArrowIconStyles,
  getErrorFilterWrapperStyles,
  getFilterWrapperStyles,
  getSelectedFilterWrapperStyles,
  getTextFieldStyles,
  iconButtonStyles,
  inputLabelStyles,
  inputStyles,
  menuControlStyles,
  menuListStyles,
  optionCheckboxStyles,
  popoverSlotProps,
} from './FilterBaseCDS.styles';
import {
  getIsSelectedValues,
  getNextSelectedValues,
  getSelectAllState,
  getSelectedOptionsLabel,
} from './FilterBaseCDS.utils';

const FilterBaseCDS = ({
  selectOptions = DEFAULT_SELECT_OPTIONS,
  filterType = FilterTypes.MULTI_SELECT,
  label = '',
  width = null,
  filterValues = null,
  setFilterValue,
  disabled = false,
  isError = false,
  isPlaceholder = false,
  withTime = false,
  dateFormat = null,
  disableFuture = false,
  withTimezoneSwitch = false,
  externalIsUtc = null,
  onExternalTimezoneChange = null,
  isClearable = false,
  dataTestId = TableTestIds.FILTER_BASE_INPUT,
  getDataTestIdOption = null,
}) => {
  const isTextFilter = filterType === FilterTypes.TEXT;
  const isMultiSelectFilter = filterType === FilterTypes.MULTI_SELECT;
  const isDateRangeFilter = filterType === FilterTypes.DATE_RANGE;
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const inputRef = useRef(null);
  const openIsUtcRef = useRef(false);

  const [selectedValues, setSelectedValues] = useState(_.isArray(filterValues) ? filterValues : []);
  const [dateStart, setDateStart] = useState(filterValues?.start || null);
  const [dateEnd, setDateEnd] = useState(filterValues?.end || null);
  const [isUtc, setIsUtc] = useState(filterValues?.isUtc || false);
  const resolvedIsUtc = !_.isNil(externalIsUtc) ? externalIsUtc : isUtc;

  const filteredSelectOptions = useMemo(
    () => selectOptions?.filter(option => option && option?.value),
    [selectOptions],
  );

  const isMenuPopoverOpened = !!anchorEl;
  const actionAnchorId = isMenuPopoverOpened ? `${label}-filter-menu-popover` : undefined;
  const isDisabled =
    isTextFilter || isDateRangeFilter ? disabled : !filteredSelectOptions?.length || disabled;
  const isDateRangeSelected = isDateRangeFilter && (!!filterValues?.start || !!filterValues?.end);

  const isSelectedValues = getIsSelectedValues({
    isTextFilter,
    filterValues,
    isDateRangeFilter,
    isDateRangeSelected,
    selectedValues,
  });

  const dateRangeDisplayValue = useMemo(() => {
    if (!isDateRangeFilter) {
      return '';
    }

    const start = filterValues?.start;
    const end = filterValues?.end;

    if (!start && !end) {
      return '';
    }

    const fmt = dateFormat;

    const formatValue = v => {
      try {
        if (!fmt) {
          return String(v ?? '');
        }

        const d = dayjs(v);

        return d.isValid() ? d.format(fmt) : String(v ?? '');
      } catch {
        return String(v ?? '');
      }
    };

    const startStr = start ? formatValue(start) : '';
    const endStr = end ? formatValue(end) : '';

    return [startStr, endStr].filter(Boolean).join(' - ');
  }, [filterValues, isDateRangeFilter, dateFormat]);

  const textFieldValue = useMemo(
    () =>
      _.isArray(selectedValues)
        ? getSelectedOptionsLabel({
            selectedValues,
            selectOptions: filteredSelectOptions,
            filterType,
          })
        : '',
    [filterType, filteredSelectOptions, selectedValues],
  );

  const selectAllState = useMemo(
    () =>
      getSelectAllState({
        selectedValues,
        selectOptions: filteredSelectOptions,
        filterType,
      }),
    [filterType, filteredSelectOptions, selectedValues],
  );

  const displayValue = useMemo(() => {
    if (isTextFilter) {
      return filterValues || '';
    }

    if (isDateRangeFilter) {
      return dateRangeDisplayValue;
    }

    return textFieldValue;
  }, [isTextFilter, isDateRangeFilter, filterValues, dateRangeDisplayValue, textFieldValue]);

  const isErrorSelected = isError && isSelectedValues;

  const customFilterWrapperStyles = useMemo(
    () => ({
      ...getFilterWrapperStyles({ isDisabled }),
      width: width || 'fit-content',
      ...getConditionalObject(isSelectedValues, () =>
        isErrorSelected
          ? getErrorFilterWrapperStyles({ isDisabled })
          : getSelectedFilterWrapperStyles({ isDisabled }),
      ),
    }),
    [isDisabled, isErrorSelected, isSelectedValues, width],
  );

  const getOnSelectOption = useCallback(
    value => () => {
      setSelectedValues(prevValues =>
        getNextSelectedValues({
          prevValues,
          value,
          filterType,
          isClearable,
          selectOptions: filteredSelectOptions,
        }),
      );
    },
    [filterType, filteredSelectOptions, isClearable],
  );

  const onConfirm = useCallback(() => {
    if (isDateRangeFilter) {
      setFilterValue({
        start: dateStart,
        end: dateEnd,
        ...(withTimezoneSwitch && { isUtc: resolvedIsUtc }),
      });
    }

    if (!isDateRangeFilter) {
      setFilterValue(selectedValues);
    }

    onClearAnchorElement();

    inputRef.current?.blur();
  }, [
    isDateRangeFilter,
    dateStart,
    dateEnd,
    resolvedIsUtc,
    withTimezoneSwitch,
    onClearAnchorElement,
    selectedValues,
    setFilterValue,
  ]);

  const onCancel = useCallback(() => {
    if (isDateRangeFilter) {
      setDateStart(filterValues?.start || null);
      setDateEnd(filterValues?.end || null);
      setIsUtc(openIsUtcRef.current);

      if (onExternalTimezoneChange) {
        onExternalTimezoneChange(openIsUtcRef.current);
      }
    }

    if (!isDateRangeFilter) {
      setSelectedValues(filterValues ?? []);
    }

    onClearAnchorElement();

    inputRef.current?.blur();
  }, [filterValues, isDateRangeFilter, onClearAnchorElement, onExternalTimezoneChange]);

  useEffect(() => {
    if (!isDateRangeFilter) {
      return;
    }

    setDateStart(filterValues?.start || null);
    setDateEnd(filterValues?.end || null);
    setIsUtc(filterValues?.isUtc || false);
  }, [filterValues, isDateRangeFilter]);

  const onClearFilter = useCallback(
    e => {
      e.stopPropagation();

      if (isTextFilter) {
        setFilterValue('');

        return;
      }

      if (isDateRangeFilter) {
        setFilterValue({ start: null, end: null });
        setDateStart(null);
        setDateEnd(null);
        setIsUtc(false);

        if (onExternalTimezoneChange) {
          onExternalTimezoneChange(false);
        }

        return;
      }

      setFilterValue([]);
      setSelectedValues([]);
    },
    [isDateRangeFilter, isTextFilter, setFilterValue, onExternalTimezoneChange],
  );

  const onDateStartChange = useCallback(value => {
    setDateStart(value);
  }, []);

  const onDateEndChange = useCallback(value => {
    setDateEnd(value);
  }, []);

  const onTimezoneChange = useCallback(
    e => {
      const { checked } = e.target;

      setIsUtc(checked);

      if (onExternalTimezoneChange) {
        onExternalTimezoneChange(checked);
      }
    },
    [onExternalTimezoneChange],
  );

  const onOpenPopover = useCallback(
    e => {
      openIsUtcRef.current = resolvedIsUtc;
      onSetAnchorElement(e);
    },
    [resolvedIsUtc, onSetAnchorElement],
  );

  const futureMaxDate = disableFuture ? dayjs() : null;
  const startMaxDate = dateEnd && dayjs(dateEnd).isBefore(futureMaxDate) ? dateEnd : futureMaxDate;

  const onTextChange = useCallback(
    e => {
      setFilterValue(e.target.value);
    },
    [setFilterValue],
  );

  const arrowIconStyles = useMemo(
    () => getArrowIconStyles({ isTextFilter, isSelectedValues, isClearable }),
    [isClearable, isSelectedValues, isTextFilter],
  );

  const textFieldSlotProps = useMemo(
    () => ({
      inputLabel: {
        shrink: false,
        sx: inputLabelStyles,
      },
      input: {
        autoFocus: false,
        sx: inputStyles,
        ref: inputRef,
        readOnly: !isTextFilter,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              disableFocusRipple
              disableRipple
              disabled={isDisabled}
              size="small"
              sx={iconButtonStyles}
              onClick={isSelectedValues && isClearable ? onClearFilter : null}
            >
              {isSelectedValues && isClearable ? (
                <ErrorFilledIcon className="selected-icon" size={IconSize[20]} />
              ) : (
                <Box sx={arrowIconStyles}>
                  <ArrowCheckIcon size={IconSize[20]} />
                </Box>
              )}
            </IconButton>
          </InputAdornment>
        ),
      },
    }),
    [arrowIconStyles, isClearable, isDisabled, isSelectedValues, isTextFilter, onClearFilter],
  );

  if (isPlaceholder) {
    return <FilterPlaceholderCDS width={width} />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box data-testid={dataTestId} sx={customFilterWrapperStyles}>
        <Tooltip disableFocusListener title={displayValue}>
          <TextField
            autoFocus={false}
            disabled={isDisabled}
            label={isSelectedValues ? '' : <Typography variant="regular16">{label}</Typography>}
            slotProps={textFieldSlotProps}
            sx={getTextFieldStyles({
              isDisabled,
              isSelected: isSelectedValues,
              isError: isErrorSelected,
            })}
            value={displayValue}
            variant="outlined"
            onChange={isTextFilter ? onTextChange : null}
            onClick={!isDisabled && !isTextFilter ? onOpenPopover : null}
          />
        </Tooltip>
        {!isTextFilter && (
          <Popover
            anchorEl={anchorEl}
            anchorOrigin={anchorOrigin}
            id={actionAnchorId}
            open={isMenuPopoverOpened}
            slotProps={popoverSlotProps}
            onClose={onCancel}
          >
            <Stack data-testid={TableTestIds.FILTER_MENU} sx={filterMenuStyles}>
              {[FilterTypes.MULTI_SELECT, FilterTypes.SINGLE_SELECT].includes(filterType) && (
                <MenuList data-testid={TableTestIds.MULTI_SELECT_MENU} sx={menuListStyles}>
                  {filteredSelectOptions.map(({ value, label, isSelectAll }) =>
                    isDecoratorOption(value) ? (
                      <DecoratorOption key={value} value={value} />
                    ) : (
                      <MenuItem
                        data-testid={getDataTestIdOption?.(value)}
                        key={value}
                        value={value}
                        onClick={getOnSelectOption(value)}
                      >
                        <Checkbox
                          checked={
                            isSelectAll && isMultiSelectFilter
                              ? selectAllState.isChecked
                              : selectedValues.includes(value)
                          }
                          indeterminate={isSelectAll && selectAllState.isIndeterminate}
                          sx={optionCheckboxStyles}
                        />
                        <ListItemText
                          primary={<Typography variant="regular16">{label}</Typography>}
                        />
                      </MenuItem>
                    ),
                  )}
                </MenuList>
              )}

              {isDateRangeFilter && (
                <>
                  <Stack
                    data-testid={TableTestIds.FILTER_BASE_DATE_RANGE}
                    sx={dateRangeContentStyles}
                  >
                    <DateFieldCDS
                      clearable
                      format={dateFormat}
                      label="Start"
                      maxDate={disableFuture ? startMaxDate : dateEnd}
                      sx={dateRangeFieldStyles}
                      value={dateStart}
                      withTime={withTime}
                      onBlur={_.noop}
                      onChange={onDateStartChange}
                      onFocus={_.noop}
                    />
                    <Typography sx={dateRangeSeparatorStyles} variant="regular14">
                      -
                    </Typography>
                    <DateFieldCDS
                      clearable
                      format={dateFormat}
                      label="End"
                      maxDate={disableFuture ? dayjs() : null}
                      minDate={dateStart}
                      sx={dateRangeFieldStyles}
                      value={dateEnd}
                      withTime={withTime}
                      onBlur={_.noop}
                      onChange={onDateEndChange}
                      onFocus={_.noop}
                    />
                  </Stack>
                  {withTimezoneSwitch && (
                    <Stack
                      data-testid={TableTestIds.DATE_TIMEZONE_SWITCH}
                      sx={dateTimezoneSwitchStyles}
                    >
                      <Typography variant="regular14">{DateTimezoneLabels.LOCAL}</Typography>
                      <Switch checked={resolvedIsUtc} size="small" onChange={onTimezoneChange} />
                      <Typography variant="regular14">{DateTimezoneLabels.UTC}</Typography>
                    </Stack>
                  )}
                </>
              )}

              <Stack
                data-testid={TableTestIds.MENU_CONTROLS_BUTTON}
                sx={isDateRangeFilter ? dateRangeMenuControlStyles : menuControlStyles}
              >
                <Button variant="line" onClick={onCancel}>
                  Cancel
                </Button>
                <Button onClick={onConfirm}>Confirm</Button>
              </Stack>
            </Stack>
          </Popover>
        )}
      </Box>
    </LocalizationProvider>
  );
};

FilterBaseCDS.propTypes = {
  disabled: PropTypes.bool,
  isError: PropTypes.bool,
  isPlaceholder: PropTypes.bool,
  isClearable: PropTypes.bool,
  filterValues: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
  selectOptions: PropTypes.array,
  filterType: PropTypes.string,
  label: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  withTime: PropTypes.bool,
  dateFormat: PropTypes.string,
  disableFuture: PropTypes.bool,
  withTimezoneSwitch: PropTypes.bool,
  externalIsUtc: PropTypes.bool,
  dataTestId: PropTypes.string,
  getDataTestIdOption: PropTypes.func,
  setFilterValue: PropTypes.func,
  onExternalTimezoneChange: PropTypes.func,
};

export default memo(FilterBaseCDS);
